import { useState, useMemo, useEffect } from 'react';
import { fetchPredictions, fetchStates } from 'api/predictions';

const PAGE_SIZE = 6;


const fmt = (n) => n ? parseInt(n).toLocaleString('en-IN') : '—';

const BUCKET_STYLES = {
  Likely:    { border: '#639922', pillBg: '#EAF3DE', pillText: '#3B6D11' },
  Uncertain:  { border: '#BA7517', pillBg: '#FAEEDA', pillText: '#854F0B' },
  Ambitious: { border: '#E24B4A', pillBg: '#FCEBEB', pillText: '#A32D2D' },
};

const BOARD_STYLES = {
  JOSAA:  { bg: '#E6F1FB', text: '#0C447C', label: 'JoSAA' },
  COMEDK: { bg: '#EAF3DE', text: '#27500A', label: 'COMEDK' },
};

function CollegeCard({ item }) {
  const s = BUCKET_STYLES[item.chance_bucket] || BUCKET_STYLES.Ambitious;
  return (
    <div
      className="mb-2 rounded-lg border bg-white px-4 py-3 shadow-sm transition-colors duration-200 hover:bg-slate-50"
      style={{
        borderColor: 'var(--color-border-tertiary)',
        borderLeft: `3px solid ${s.border}`,
      }}
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex-1 text-sm font-semibold leading-snug text-primary-900">
          {item.instname}
        </div>
        <span
          className="shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ background: s.pillBg, color: s.pillText }}
        >
          {item.chance_bucket}
        </span>
      </div>
      <div className="mb-2 text-xs leading-snug text-secondary-700">
        {item.primedname}
      </div>
      <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <div>
          <div className="text-[11px] text-secondary-700">Predicted closing</div>
          <div className="text-sm font-semibold text-primary-900">
            {fmt(item.predicted_closing)}
          </div>
          <div className="text-[11px] text-secondary-700">
            {fmt(item.band_low)} – {fmt(item.band_high)}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-secondary-700">Confidence · Seats</div>
          <div className="text-xs font-semibold capitalize text-primary-900">
            {item.trend_confidence}
          </div>
          <div className="text-[11px] text-secondary-700">
            {item.total_seats ? `${item.total_seats} seats` : '—'} · {item.statequota}
          </div>
        </div>
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, onFirst, onPrev, onNext }) {
  const btnClass =
    'rounded-md border border-primary-200 bg-white px-3 py-1.5 text-xs font-semibold text-primary-900 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnClass} onClick={onFirst} disabled={page === 0}>
          First page
        </button>
        <button type="button" className={btnClass} onClick={onPrev} disabled={page === 0}>
          Previous
        </button>
        <button type="button" className={btnClass} onClick={onNext} disabled={page >= totalPages - 1}>
          Next page
        </button>
      </div>
      <span className="text-xs font-medium text-secondary-700">
        Page {page + 1} of {totalPages}
        {totalPages > 1 && ` · ${PAGE_SIZE} per page`}
      </span>
    </div>
  );
}

function BoardResults({ board, rank, results }) {
  const bs = BOARD_STYLES[board];
  const [page, setPage] = useState(0);
  const [collegeFilter, setCollegeFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [bucketFilter, setBucketFilter] = useState('');

  const likely = results.filter(r => r.chance_bucket === 'Likely');
  const uncertain = results.filter(r => r.chance_bucket === 'Uncertain');
  const ambitious = results.filter(r => r.chance_bucket === 'Ambitious');

  const filtered = useMemo(() => {
    const collegeQ = collegeFilter.trim().toLowerCase();
    const branchQ = branchFilter.trim().toLowerCase();
    return results.filter((item) => {
      if (bucketFilter && item.chance_bucket !== bucketFilter) return false;
      if (collegeQ && !(item.instname || '').toLowerCase().includes(collegeQ)) return false;
      if (branchQ && !(item.primedname || '').toLowerCase().includes(branchQ)) return false;
      return true;
    });
  }, [results, collegeFilter, branchFilter, bucketFilter]);

  useEffect(() => {
    setPage(0);
  }, [collegeFilter, branchFilter, bucketFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  );

  return (
    <div className="mb-6 rounded-xl border bg-white p-4 shadow-sm" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <div className="mb-3 flex items-center gap-2">
        <span
          className="rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: bs.bg, color: bs.text }}
        >
          {bs.label}
        </span>
        <span className="ml-auto text-xs text-secondary-700">
          Rank {fmt(rank)}
        </span>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        {[
          { label: 'Likely', count: likely.length, color: '#3B6D11' },
          { label: 'Uncertain', count: uncertain.length, color: '#854F0B' },
          { label: 'Ambitious', count: ambitious.length, color: '#A32D2D' },
        ].map(({ label, count, color }) => (
          <div key={label} className="rounded-md bg-slate-50 p-2 text-center">
            <div className="text-lg font-semibold" style={{ color }}>{count}</div>
            <div className="mt-0.5 text-[11px] text-secondary-700">{label}</div>
          </div>
        ))}
      </div>

      <div className="mb-3">
        <div className="mb-2 text-sm font-semibold text-primary-900">Filter colleges</div>
        <FormRow cols={3}>
          <Field label="College name">
            <input
              className={inputClassName()}
              type="text"
              value={collegeFilter}
              onChange={(e) => setCollegeFilter(e.target.value)}
              placeholder="Search by college…"
            />
          </Field>
          <Field label="Branch">
            <input
              className={inputClassName()}
              type="text"
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              placeholder="Search by branch…"
            />
          </Field>
          <Field label="Chance">
            <select
              className={inputClassName()}
              value={bucketFilter}
              onChange={(e) => setBucketFilter(e.target.value)}
            >
              <option value="">All (Likely · Uncertain · Ambitious)</option>
              <option value="Likely">Likely</option>
              <option value="Uncertain">Uncertain</option>
              <option value="Ambitious">Ambitious</option>
            </select>
          </Field>
        </FormRow>
        <div className="text-xs text-secondary-700">
          Showing {filtered.length === 0 ? 0 : safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} colleges
          {filtered.length !== results.length && ` (filtered from ${results.length})`}
        </div>
      </div>

      <div className="mb-2 text-sm font-semibold text-primary-900">Your colleges</div>
      {pageItems.length > 0 ? (
        pageItems.map((item, i) => (
          <CollegeCard key={`${item.instcode}-${item.primedname}-${safePage}-${i}`} item={item} />
        ))
      ) : (
        <div className="rounded-md border border-primary-100 bg-primary-50 px-3 py-3 text-sm text-primary-800">
          No colleges match your filters. Try clearing or adjusting the search.
        </div>
      )}

      {filtered.length > 0 && (
        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          onFirst={() => setPage(0)}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        />
      )}
    </div>
  );
}

function BoardRow({ badge, label, subtitle, children, disabled }) {
  return (
    <div
      className={`grid gap-3 border-b border-slate-200 px-3 py-3 last:border-b-0 sm:px-4 ${disabled ? 'pointer-events-none opacity-55' : ''} lg:grid-cols-12 lg:items-start`}
    >
      <div className="lg:col-span-3">
        <div
          className="inline-flex rounded-md px-3 py-1.5 text-sm font-bold shadow-sm"
          style={{ background: badge.bg, color: badge.text }}
        >
          {label}
        </div>
        <div className="mt-1 text-sm text-secondary-700">{subtitle}</div>
        {disabled && (
          <div className="mt-1 text-xs font-semibold text-stone-600">Coming soon</div>
        )}
      </div>
      <div className="lg:col-span-9">
        {children}
      </div>
    </div>
  );
}

function FormRow({ children, cols = 2 }) {
  const colsClass =
    cols === 1
      ? 'grid-cols-1'
      : cols === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : cols === 3
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          : cols === 4
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
        : 'grid-cols-1 sm:grid-cols-3';

  return (
    <div className={`mb-2 grid gap-2 sm:gap-3 ${colsClass}`}>
      {children}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className="flex flex-col gap-1" style={{ gridColumn: full ? '1 / -1' : undefined }}>
      <label className="text-sm font-medium text-secondary-700">{label}</label>
      {children}
    </div>
  );
}

function inputClassName() {
  return 'w-full rounded-md border border-primary-200 bg-white px-3 py-2 text-sm text-primary-900 shadow-sm outline-none transition-all duration-200 placeholder:text-secondary-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-100';
}

export default function PredictionPage() {
  const [josaa, setJosaa] = useState({ rank: '', statequota: 'AI', caste: 'OPEN', gender: 'GENDER-NEUTRAL'});
  const [comedk, setComedk] = useState({ rank: '', caste: 'GM'});
  const [states, setStates] = useState([]);
  const [homeState, setHomeState] = useState();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [didSearch, setDidSearch] = useState(false);

  const jChange = (e) => setJosaa({ ...josaa, [e.target.name]: e.target.value });
  const cChange = (e) => setComedk({ ...comedk, [e.target.name]: e.target.value });

  const findColleges = async () => {
    console.log(comedk)
    if (!josaa.rank && !comedk.rank) {
      setError('Please enter at least one rank');
      return;
    }

    if (josaa.statequota != 'AI' && !homeState) {
      setError('Please enter your Home State');
      return;
    }

    setLoading(true);
    setError(null);
    setDidSearch(true);
    setResults([]);

    const calls = [];
    // Calls.push to collect an array of commands and execute them together.
    if (josaa.rank) calls.push({ board: 'JOSAA', 
                                  rank: josaa.rank, 
                                  statequota: josaa.statequota, 
                                  caste: josaa.caste, 
                                  gender: josaa.gender,
                                  ...(josaa.statequota === 'HS' && homeState ? { home_state:homeState} :{}),
                                  ...(josaa.statequota === 'OS' && homeState ? { exclude_state:homeState} :{}),
    });
    if (comedk.rank) calls.push({ board: 'COMEDK', rank: comedk.rank, caste: comedk.caste, statequota: 'KA' });
    console.log(comedk)
    try {
      const responses = await Promise.all(
            calls.map(async p => fetchPredictions(p))
      );
      setResults(calls.map((p, i) => ({
        board: p.board,
        rank: p.rank,
        data: responses[i].results || [],
      })));
    } catch (e) {
      setError(`Could not load predictions. ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const whatsappCTA = () => {
      const boards = results.filter(r => r.data.length > 0);
    
      const summary = boards.map(r => {
        const likely = r.data.filter(d => d.chance_bucket === 'Likely').length;
        const uncertain = r.data.filter(d => d.chance_bucket === 'Uncertain').length;
    
        return `${r.board}: Rank ${fmt(r.rank)} — ${likely} Likely, ${uncertain} Uncertain`;
      }).join('\n');
    
      const msg = encodeURIComponent(
                  `Hi College Avenue! I'd like my full college prediction PDF (₹49).
                  \n\n ${summary}
                  \n\n Please send me the payment link and full report.`
                  );
    
      const number = process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\D/g, '');
      const whatsapp_url = `https://wa.me/${number}?text=${msg}`
      window.open(whatsapp_url, '_blank');
      
  };
  
  const hasResults = results.some(r => r.data.length > 0);
  useEffect(() => {fetchStates()
                      .then(data => setStates(data))
                      .catch(e => console.error('Could not load states', e));
  }, []);
  console.log(states)
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-4 sm:px-6 sm:py-6 md:px-8">
      <div className="mb-1 text-2xl font-bold text-primary-900 sm:text-3xl">Find your colleges</div>
      <div className="mb-5 text-sm text-secondary-700 sm:text-base">
          Enter your home state.
          <select name="homeState" className={inputClassName()} value={homeState} 
                                   onChange={e => setHomeState(e.target.value)} 
                                   style={{ width: '100%' }}>
                  <option value="">Select your state</option>
                  {states.map(s => (
                    <option key={s.id} value={s.state_name}>{s.state_name}</option>
                  ))}
          </select>
      </div>
      <div className="mb-5 text-sm text-secondary-700 sm:text-base">
        Enter ranks for the exams you appeared for. Leave others blank.
      </div>

      <div className="mb-4 rounded-xl bg-white shadow-md">
        <BoardRow badge={{ bg: '#E6F1FB', text: '#0C447C' }} label="JoSAA" subtitle="IITs · NITs · IIITs">
          <FormRow cols={4}>
            <Field label="JEE rank">
              <input className={inputClassName()} type="number" name="rank" value={josaa.rank} 
                     onChange={jChange} placeholder="e.g. 31482" />
            </Field>
            <Field label="Quota">
              <select className={inputClassName()} name="statequota" value={josaa.statequota} 
                      onChange={jChange}>
                <option value="AI">All India</option>
                <option value="HS">Home state</option>
                <option value="OS">Other state</option>
              </select>
            </Field>
            <Field label="Category">
              <select className={inputClassName()} name="caste" value={josaa.caste} 
                      onChange={jChange}>
                <option value="OPEN">Open</option>
                <option value="OBC-NCL">OBC-NCL</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
                <option value="EWS">EWS</option>
              </select>
            </Field>
            <Field label="Gender">
              <select className={inputClassName()} name="gender" value={josaa.gender} 
                      onChange={jChange}>
                <option value="GENDER-NEUTRAL">Gender neutral</option>
                <option value="FEMALE-ONLY (INCLUDING SUPERNUMERARY)">Female only</option>
              </select>
            </Field>
          </FormRow>
        </BoardRow>

        <BoardRow badge={{ bg: '#EAF3DE', text: '#27500A' }} label="COMEDK" subtitle="Karnataka engineering colleges">
          <FormRow cols={2}>
            <Field label="COMEDK rank">
              <input className={inputClassName()} type="number" name="rank" value={comedk.rank} 
                      onChange={cChange} placeholder="Leave blank if not appeared" />
            </Field>
            <Field label="Category">
              <select className={inputClassName()} name="caste" value={comedk.caste} 
                       onChange={cChange}>
                <option value="GM">General Merit (GM)</option>
                <option value="HKR/KKR">HKR/KKR (Hyderabad/Kalyana Karnataka Region)</option>
              </select>
            </Field>
          </FormRow>
        </BoardRow>

        <BoardRow badge={{ bg: '#F1EFE8', text: '#444441' }} label="MHT-CET" subtitle="Maharashtra colleges" disabled>
          <FormRow cols={1}>
            <Field label="MHT-CET rank" full>
              <input className={inputClassName()} type="number" placeholder="Coming soon" disabled />
            </Field>
          </FormRow>
        </BoardRow>
      </div>

      <button
        onClick={findColleges}
        disabled={loading}
        className="mb-5 w-full rounded-md bg-primary-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-primary-800 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
      >
        {loading ? 'Finding colleges…' : 'Find my colleges →'}
      </button>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {didSearch && !loading && !error && !hasResults && (
        <div className="mb-4 rounded-md border border-primary-100 bg-primary-50 px-3 py-3 text-sm text-primary-800">
          No matches found for the selected details. Try updating rank, category, or gender filters.
        </div>
      )}

      {hasResults && (
        <>
          {results.map(r => r.data.length > 0 && (
            <BoardResults key={r.board} board={r.board} rank={r.rank} results={r.data} />
          ))}

          <hr className="my-4 border-0 border-t" style={{ borderColor: 'var(--color-border-tertiary)' }} />

          <button
            onClick={whatsappCTA}
            className="mb-2 flex w-full items-center justify-center gap-2 rounded-md bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#1FA855] hover:shadow-md"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21"/>
              <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1"/>
            </svg>
            Receive the Full List on WhatsApp • ₹49
          </button>
          <div className="mb-5 text-center text-xs text-secondary-700">
            Get top 10 colleges per exam as a PDF · delivered on WhatsApp
          </div>

          <div className="border-t pt-4 text-xs leading-relaxed text-secondary-600" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            Predictions based on 3 years of closing rank data (AY2023–26). High confidence ±25% covers 88% of historical outcomes. Medium ±30% covers 86%. Use as a guide, not a guarantee.
          </div>
        </>
      )}
    </div>
  );
}